import { DownloadSimpleIcon, PlusIcon } from '@phosphor-icons/react'
import { debounce } from 'lodash'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import api from '@/api/api'
import { getClientsWithCount } from '@/api/clients/get-clients-with-count'
import GiftCardsList from '@/components/BuildingDashboard/Rewards/components/GiftCardsList/GiftCardsList'
import {
  getCsvFields,
  getGiftCardCategoryFilterOptions,
  getGiftCardStatuses,
  getGiftCardsTabs,
} from '@/components/BuildingDashboard/Rewards/helpers'
import RewardsGiftDetailsModal from '@/components/BuildingDashboard/Rewards/RewardsGiftDetailsModal/RewardsGiftDetailsModal'
import RewardsSendGiftModal from '@/components/BuildingDashboard/Rewards/RewardsSendGiftModal/RewardsSendGiftModal'
import Filter from '@/components/Filter/Filter'
import SearchField from '@/components/FormLegacy/FormFieldsV2/SearchField'
import PaginationFooter from '@/components/PaginationFooter/PaginationFooter'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { useSelectOptions } from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { popupFactory } from '@/factories'
import { useSnackbar } from '@/helpers/hooks/useSnackbar'
import { useFilters, useSearchParams } from '@/hooks'
import { fetchRewardsHistory, selectRewards } from '@/reducers/rewardsReducer'
import { useAppDispatch } from '@/store/hooks'
import { buildCsvFilename, downloadCsv } from '@/utils/file'

import { giftCardsFiltersSchema, initialGiftCardsFilters } from './gift-cards-table.constants'
import * as S from './gift-cards-table.styled'
import { GiftCardsTableProps } from './gift-cards-table.types'

interface ResidentFilterOption {
  id: number
  text: string
  value: number
}

export const GiftCardsTable = ({ resident, building }: GiftCardsTableProps) => {
  const { t } = useTranslation(['rewards-table', 'rewards-dashboard'])
  const dispatch = useAppDispatch()
  const rewards = useSelector(selectRewards) as unknown[]
  const { displaySnackbar } = useSnackbar()

  const residentId = resident?.id

  const giftCardsTabs = getGiftCardsTabs(t)
  const giftCardCategoryFilterOptions = getGiftCardCategoryFilterOptions(t)
  const giftCardStatuses = getGiftCardStatuses(t)

  const [isLoading, setLoading] = useState(true)
  const [paginationItemsCount, setPaginationItemsCount] = useState(0)
  const [isShowSendGift, setShowSendGift] = useState(false)
  // Note: kept separate from the URL-backed `search` filter below so the input reflects every
  // keystroke instantly, while the filter (and the resulting refetch/URL write) is debounced.
  const [searchInput, setSearchInput] = useState('')

  const searchParams = useSearchParams({
    initialState: initialGiftCardsFilters,
    schema: giftCardsFiltersSchema,
  })

  const filters = useFilters({
    controlledState: searchParams.parsedQuery,
    initialState: initialGiftCardsFilters,
    onFiltersChange: searchParams.writeParams,
  })

  useEffect(() => {
    setSearchInput(filters.getFilter('search') ?? '')
  }, [])

  const type = filters.getFilter('type')
  const category = filters.getFilter('category')
  const status = filters.getFilter('status')
  const search = filters.getFilter('search')
  const skip = filters.getFilter('skip') ?? 0
  const limit = filters.getFilter('limit') ?? initialGiftCardsFilters.limit!
  const clientId = residentId ? [residentId] : filters.getFilter('clientId')

  const buildingId = building?.id

  // Note: `setFilter` is recreated every render (see `useFilters`) - stashing the latest
  // reference in a ref lets the debounced callback below stay a single stable instance across
  // renders instead of re-debouncing (and losing pending calls) on every keystroke.
  const setFilterRef = useRef(filters.setFilter)
  setFilterRef.current = filters.setFilter

  const debouncedSetSearch = useMemo(
    () => debounce((value: string) => setFilterRef.current('search', value), 500),
    [],
  )
  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch])

  const fetchRewards = async () => {
    if (!buildingId) return

    setLoading(true)
    try {
      const count = await dispatch(
        fetchRewardsHistory({
          buildingId,
          category: category || undefined,
          clientId,
          limit,
          query: search || undefined,
          skip,
          status: status || undefined,
          type: type || undefined,
        }),
      )
      setPaginationItemsCount(count as number)
    } catch (error) {
      displaySnackbar((error as Error).message, true)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (buildingId) void fetchRewards()
  }, [buildingId, type, category, status, search, skip, limit, residentId])

  const handleTabChange = (tabFilter: { value: string }) => {
    filters.setFilter('type', tabFilter.value)
  }

  const handleCategoryChange = (option: { value: string }) => {
    filters.setFilter('category', option.value)
  }

  const handleStatusChange = (option: { value: string }) => {
    filters.setFilter('status', option.value)
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value)
    debouncedSetSearch(e.target.value)
  }

  const residentsQuery = useSelectOptions(
    {
      query: (params, signal) => getClientsWithCount({ ...params, buildingId }, signal),
      transform: (data) =>
        (data?.clients ?? []).map((client) => ({
          label: `${client.user.firstName} ${client.user.lastName}`,
          value: Number(client.id),
        })),
    },
    {
      enabled: !!buildingId && !residentId,
      queryKey: ['getClientsWithCount', buildingId],
    },
  )

  const residentOptions: ResidentFilterOption[] = residentsQuery.selectProps.options.map(
    (option) => ({ id: option.value, text: option.label, value: option.value }),
  )

  const handleResidentChange = (options: ResidentFilterOption | ResidentFilterOption[]) => {
    const selected = Array.isArray(options) ? options : [options]
    filters.setFilter(
      'clientId',
      selected.map((option) => option.value),
    )
  }

  const selectedTabOption = giftCardsTabs.find((tabFilter) => tabFilter.value === type)
  const selectedCategoryOption =
    giftCardCategoryFilterOptions.find((option) => option.value === category) ??
    giftCardCategoryFilterOptions[0]
  const selectedStatusOption =
    giftCardStatuses.find((option) => option.value === status) ?? giftCardStatuses[0]
  const selectedResidentOptions = residentOptions.filter((option) =>
    (filters.getFilter('clientId') ?? []).includes(option.value),
  )

  const residentFilterPlaceholder = filters.getFilter('clientId')?.length
    ? t('rewards-table:residentSelectedCount', { count: filters.getFilter('clientId')!.length })
    : t('rewards-table:residentPlaceholder')

  // Note: `data` is whatever the legacy `FeaturedTable` row-click handler passes - either the raw
  // gift card row, or a react-table row wrapper with the raw row under `.original`.
  // biome-ignore lint/suspicious/noExplicitAny: legacy untyped `FeaturedTable` row shape
  const onSelectCard = (data: any) => handleOpenGiftCardDetails(data?.original.id ?? data.id)

  const onSendEmail = (rewardId: string) => {
    api
      .resendGiftCardEmail(rewardId)
      .then(() => displaySnackbar(t('rewards-table:emailSentSuccess')))
      .catch(() => displaySnackbar(t('rewards-table:emailSentError'), true))
  }

  const handleCSVDownload = () => {
    if (!rewards.length || !building) {
      displaySnackbar(t('rewards-table:noDataToDownload'), true)
      return
    }
    downloadCsv({
      data: rewards,
      fields: getCsvFields(building.timezone, t),
      filename: buildCsvFilename({ building: building.name, date: new Date(), page: 'rewards' }),
    })
  }

  const handleOpenGiftCardDetails = (cardId: number) => {
    if (!building) {
      return
    }

    popupFactory.open(RewardsGiftDetailsModal, {
      fetchRewards: fetchRewards,
      id: cardId,
      timezone: building.timezone!,
    })
  }

  return (
    <>
      {/* Note: Resident Profile embed - the filters row below is already at full width, so
      title + "Send gift" get their own row above it (see EL-14182 hidden-filter pattern). */}
      {residentId && (
        <S.EmbedHeader>
          <Typography as="h2" size="heading-small" color="black800">
            {t('rewards-dashboard:giftCardsTitle')}
          </Typography>
          <Button size="small" onClick={() => setShowSendGift(true)}>
            {t('rewards-table:sendGiftButton')}
            <Icon icon={PlusIcon} />
          </Button>
        </S.EmbedHeader>
      )}
      <div className="rewards-table">
        <div className="rewards-table__wrap">
          <div className="rewards-table__filters">
            <div className="rewards-table__filters__group">
              {giftCardsTabs.map((tabFilter) => (
                <button
                  key={tabFilter.id}
                  className={`rewards-filter-item ${
                    selectedTabOption && tabFilter.id === selectedTabOption.id ? 'active' : ''
                  }`}
                  disabled={isLoading}
                  type="button"
                  onClick={() => handleTabChange(tabFilter)}
                >
                  <p style={{ display: 'inline-block' }}>{tabFilter.text} </p>
                </button>
              ))}
            </div>
            <div className="rewards-table__filters__group">
              <Filter
                closeAfterSelect
                placeholder={selectedCategoryOption.text}
                options={giftCardCategoryFilterOptions}
                disabled={isLoading}
                value={[selectedCategoryOption]}
                name="GiftCardCategoryFilter"
                additionalClass="rewards-table__filters__options-filter gift-card-category-filter"
                variant="borderless"
                onChange={(e) => handleCategoryChange(e.target.value as { value: string })}
              />
              <Filter
                closeAfterSelect
                placeholder={selectedStatusOption.text}
                options={giftCardStatuses}
                disabled={isLoading}
                value={[selectedStatusOption]}
                name="GiftStatusFilter"
                additionalClass="rewards-table__filters__options-filter"
                variant="borderless"
                onChange={(e) => handleStatusChange(e.target.value as { value: string })}
              />
              {!residentId && (
                <Filter
                  placeholder={residentFilterPlaceholder}
                  options={residentOptions}
                  disabled={isLoading}
                  value={selectedResidentOptions}
                  name="GiftCardResidentFilter"
                  additionalClass="rewards-table__filters__options-filter"
                  variant="borderless"
                  config={{ hasSearch: true, multiple: true }}
                  handleSearch={residentsQuery.selectProps.onSearchChange}
                  searchValue={residentsQuery.selectProps.search}
                  closeAfterSelect={false}
                  onChange={(e) =>
                    handleResidentChange(
                      e.target.value as ResidentFilterOption | ResidentFilterOption[],
                    )
                  }
                />
              )}
              <div className="rewards-table__filters__search">
                <SearchField
                  placeholder={t('rewards-table:searchPlaceholder')}
                  value={searchInput}
                  onChange={handleSearchChange}
                />
              </div>
              <button
                className="button button-black-outline"
                disabled={!rewards.length || isLoading}
                type="button"
                onClick={handleCSVDownload}
              >
                <Icon icon={DownloadSimpleIcon} size="2.4rem" />
              </button>
              {!residentId && (
                <button
                  className="button button-black"
                  disabled={!building}
                  type="button"
                  onClick={() => setShowSendGift(true)}
                >
                  {t('rewards-table:sendGiftButton')}
                </button>
              )}
            </div>
          </div>
          <GiftCardsList
            data={rewards}
            isLoading={isLoading}
            building={building}
            onSelectCard={onSelectCard}
            onSendEmail={onSendEmail}
          />
          {rewards.length > 0 && (
            <PaginationFooter
              showOnPage={limit}
              pageNumber={Math.floor(skip / limit) + 1}
              paginationItemsCount={paginationItemsCount}
              setSiteLoading={setLoading}
              setShowOnPage={(newLimit: number) => filters.setFilters({ limit: newLimit, skip: 0 })}
              setPageNumber={(page: number) => filters.setFilter('skip', (page - 1) * limit)}
            />
          )}
          {isShowSendGift && (
            <RewardsSendGiftModal
              selectedBuilding={building}
              lockResident={
                resident
                  ? {
                      email: resident.email,
                      label: resident.name,
                      phone: resident.phone ?? undefined,
                      value: resident.id,
                    }
                  : undefined
              }
              onClose={() => setShowSendGift(false)}
              onSuccess={() => void fetchRewards()}
            />
          )}
        </div>
      </div>
    </>
  )
}
